import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  PenLine, X, Upload, FileText, CheckCircle2, ChevronRight, ChevronLeft, Plus, Send, Loader2,
  ListOrdered, Eye, BellRing, CalendarClock, Settings2,
} from "lucide-react";
import { api, errMsg } from "@/lib/api";
import {
  PlacementEditor, TeamChips, RosterSearch, ViewerPanel, Toggle, useTeam, todayISO, fmtDay, SIGNER_COLORS, FIELD_TYPES,
} from "@/pages/dashboard/Signatures";

const titleFromFile = (name) => (name || "").replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim()
  .replace(/\b\w/g, (c) => c.toUpperCase());

// Foot of the last page, two columns, bottom-up: signature + date per signer. Draggable afterwards.
export const footPlacement = (signers, pageCount) => {
  const sig = FIELD_TYPES.find((t) => t.kind === "signature");
  const date = FIELD_TYPES.find((t) => t.kind === "date");
  const page = Math.max(1, pageCount || 1);
  return signers.map((s, i) => {
    if ((s.fields || []).some((f) => f.kind === "signature")) return s;
    const col = i % 2, row = Math.floor(i / 2);
    const x = 0.06 + col * 0.47;
    const y = Math.max(0.04, 0.86 - row * 0.085);
    return { ...s, fields: [
      { kind: "signature", page, x, y, w: sig.w, h: sig.h },
      { kind: "date", page, x: x + sig.w + 0.02, y: y + 0.012, w: date.w, h: date.h },
    ] };
  });
};

const Steps = ({ step }) => (
  <ol className="flex items-center gap-2 text-xs font-bold" data-testid="qs-steps">
    {["Document", "Signers", "Review & send"].map((l, i) => (
      <li key={l} className="flex items-center gap-2">
        <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] ${step > i + 1 ? "bg-emerald-600 text-white" : step === i + 1 ? "bg-ink text-white" : "bg-sand-200 text-ink/50"}`}>{step > i + 1 ? "✓" : i + 1}</span>
        <span className={step === i + 1 ? "text-ink" : "text-ink/45"}>{l}</span>
        {i < 2 && <ChevronRight className="h-3 w-3 text-ink/30" />}
      </li>
    ))}
  </ol>
);

function DocumentStep({ tenantId, title, setTitle, file, setFile, pickedDoc, setPickedDoc }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get(`/tenants/${tenantId}/documents`).then((r) =>
      setDocs((r.data || []).filter((d) => (d.filename || "").toLowerCase().endsWith(".pdf") && d.storage_path))).catch(() => {});
  }, [tenantId]);
  const pickFile = (f) => { if (!f) return; setFile(f); setPickedDoc(""); setTitle((t) => t || titleFromFile(f.name)); };
  return (
    <div className="max-w-xl space-y-4">
      <label data-testid="qs-upload" onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
        className={`flex cursor-pointer items-center gap-3 rounded-2xl border-2 border-dashed px-5 py-6 transition-colors ${file ? "border-emerald-300 bg-emerald-50" : "border-sand-300 bg-white hover:border-ink/30"}`}>
        <Upload className="h-6 w-6 text-ink/40" />
        <div>
          <div className="text-sm font-bold">{file ? file.name : "Drop a PDF here or click to upload"}</div>
          <div className="text-xs text-ink/50">{file ? "Ready — you can still change the title below" : "One PDF, everyone signs the same copy"}</div>
        </div>
        <input type="file" accept="application/pdf" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
      </label>
      {docs.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-ink/40">or pick from your Documents</p>
          <div className="max-h-44 divide-y divide-sand-100 overflow-y-auto rounded-xl border border-sand-200 bg-white">
            {docs.map((d) => (
              <button key={d.id} data-testid={`qs-pick-doc-${d.id}`} type="button"
                onClick={() => { const on = pickedDoc === d.id; setPickedDoc(on ? "" : d.id); setFile(null); if (!on) setTitle(d.title || ""); }}
                className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-sand-50 ${pickedDoc === d.id ? "bg-emerald-50" : ""}`}>
                <FileText className="h-4 w-4 shrink-0 text-ink/40" />
                <span className="flex-1 truncate font-semibold">{d.title}</span>
                {pickedDoc === d.id && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <label className="text-sm font-semibold">Title <span className="font-normal text-ink/40">— what signers see</span></label>
        <input data-testid="qs-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder='e.g. "2026 Budget Approval"'
          className="mt-1 w-full rounded-xl border border-sand-200 bg-white px-3 py-2.5 outline-none focus:border-ink/30" />
      </div>
    </div>
  );
}

function SignersStep({ tenantId, signers, setSigners, sequential, setSequential, viewers, setViewers, showCc, setShowCc }) {
  const team = useTeam(tenantId);
  const add = (s = { name: "", phone: "", email: "" }) => {
    if (signers.length >= 30) { toast.info("Up to 30 signers on one document — for more, use the advanced editor."); return; }
    if (s.name && signers.some((x) => x.name === s.name && (x.phone === (s.phone || "") || x.email === (s.email || "")))) { toast.info(`${s.name} is already a signer`); return; }
    setSigners((prev) => [...prev, { name: s.name || "", phone: s.phone || "", email: s.email || "", fields: [] }]);
  };
  const update = (i, k, v) => setSigners((prev) => prev.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const move = (i, dir) => setSigners((prev) => { const j = i + dir; if (j < 0 || j >= prev.length) return prev; const a = [...prev]; [a[i], a[j]] = [a[j], a[i]]; return a; });
  return (
    <div className="max-w-2xl space-y-4">
      <div className="space-y-2" data-testid="qs-signers">
        {signers.length === 0 && <p className="rounded-xl border border-dashed border-sand-300 px-4 py-3 text-sm text-ink/50">Nobody yet — add people below.</p>}
        {signers.map((s, i) => (
          <div key={i} data-testid={`qs-signer-${i}`} className="grid grid-cols-[auto_1.3fr_1fr_1fr_auto] items-center gap-2 rounded-xl border border-sand-200 bg-white px-3 py-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-black text-white" style={{ background: SIGNER_COLORS[i % SIGNER_COLORS.length] }}>{sequential ? i + 1 : ""}</span>
            <input data-testid={`qs-signer-name-${i}`} value={s.name} onChange={(e) => update(i, "name", e.target.value)} placeholder="Full name" className="min-w-0 bg-transparent text-sm font-semibold outline-none placeholder:font-normal" />
            <input data-testid={`qs-signer-phone-${i}`} value={s.phone} onChange={(e) => update(i, "phone", e.target.value)} placeholder="Mobile" className="min-w-0 rounded-lg border border-sand-200 px-2 py-1 text-xs outline-none focus:border-ink/30" />
            <input data-testid={`qs-signer-email-${i}`} value={s.email} onChange={(e) => update(i, "email", e.target.value)} placeholder="Email" className="min-w-0 rounded-lg border border-sand-200 px-2 py-1 text-xs outline-none focus:border-ink/30" />
            <span className="flex items-center gap-1">
              {sequential && <>
                <button type="button" data-testid={`qs-signer-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="text-ink/30 hover:text-ink disabled:opacity-20 text-xs">↑</button>
                <button type="button" data-testid={`qs-signer-down-${i}`} onClick={() => move(i, 1)} disabled={i === signers.length - 1} className="text-ink/30 hover:text-ink disabled:opacity-20 text-xs">↓</button>
              </>}
              <button type="button" data-testid={`qs-signer-remove-${i}`} onClick={() => setSigners((prev) => prev.filter((_, j) => j !== i))} className="text-ink/30 hover:text-red-600"><X className="h-4 w-4" /></button>
            </span>
          </div>
        ))}
        <button type="button" data-testid="qs-add-signer" onClick={() => add()}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-sand-300 py-2 text-sm font-semibold text-ink/60 hover:border-ink/40 hover:text-ink">
          <Plus className="h-4 w-4" /> Add a signer by name
        </button>
      </div>
      <TeamChips team={team} onPick={add} prefix="qs" />
      <RosterSearch tenantId={tenantId} onPick={add} prefix="qs" />
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-sand-200 pt-3 text-xs font-semibold text-ink/60">
        <label className="flex cursor-pointer items-center gap-2" title="Each signer is invited only after the one before them signs">
          <ListOrdered className="h-4 w-4" /> Sign in order <Toggle testid="qs-sequential" on={sequential} onClick={() => setSequential(!sequential)} />
        </label>
        {!showCc ? (
          <button type="button" data-testid="qs-show-cc" onClick={() => setShowCc(true)} className="flex items-center gap-1 hover:text-ink"><Eye className="h-4 w-4" /> + Copy someone (view only)</button>
        ) : null}
      </div>
      {showCc && <ViewerPanel tenantId={tenantId} viewers={viewers} setViewers={setViewers} />}
    </div>
  );
}

function OptionsLine({ opts, setOpts, sequential }) {
  const [open, setOpen] = useState(false);
  const notify = [opts.notifyOnSign ? "each signature" : null, opts.notifyOnDecline ? "declines" : null].filter(Boolean);
  return (
    <div data-testid="qs-options" className="rounded-2xl border border-sand-200 bg-white px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <BellRing className="h-4 w-4 text-redline-500" />
        <span data-testid="qs-options-summary" className="text-ink/70">
          Reminders every {opts.cadence === 1 ? "day" : opts.cadence === 7 ? "week" : `${opts.cadence} days`}, up to {opts.maxRem}× · {opts.expires ? `void after ${fmtDay(opts.expires)}` : "no deadline"} · {notify.length ? `tell me about ${notify.join(" and ")}` : "tell me when everyone has signed"}{sequential ? " · signing in order" : ""}
        </span>
        <button type="button" data-testid="qs-options-toggle" onClick={() => setOpen(!open)} className="ml-auto inline-flex items-center gap-1 text-xs font-bold text-redline-600 hover:text-redline-700">
          <Settings2 className="h-3.5 w-3.5" /> {open ? "Done" : "Change"}
        </button>
      </div>
      {open && (
        <div className="mt-3 grid gap-3 border-t border-sand-100 pt-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-xs font-semibold text-ink/60">Remind every
            <select data-testid="qs-cadence" value={opts.cadence} onChange={(e) => setOpts({ ...opts, cadence: Number(e.target.value) })} className="rounded-lg border border-sand-200 bg-white px-2 py-1.5 text-sm font-semibold text-ink">
              <option value={1}>day</option><option value={2}>2 days</option><option value={3}>3 days</option><option value={7}>week</option>
            </select>
            up to
            <select data-testid="qs-max-reminders" value={opts.maxRem} onChange={(e) => setOpts({ ...opts, maxRem: Number(e.target.value) })} className="rounded-lg border border-sand-200 bg-white px-2 py-1.5 text-sm font-semibold text-ink">
              {[0, 1, 2, 3, 5, 8, 10].map((n) => <option key={n} value={n}>{n}×</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-ink/60"><CalendarClock className="h-4 w-4 text-redline-500" /> Void if not signed by
            <input data-testid="qs-expires" type="date" value={opts.expires} min={todayISO()} onChange={(e) => setOpts({ ...opts, expires: e.target.value })} className="rounded-lg border border-sand-200 bg-white px-2 py-1.5 text-sm font-semibold text-ink" />
            {opts.expires && <button type="button" data-testid="qs-expires-clear" onClick={() => setOpts({ ...opts, expires: "" })} className="text-ink/30 hover:text-ink"><X className="h-4 w-4" /></button>}
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-ink/60"><Toggle testid="qs-notify-on-sign" on={opts.notifyOnSign} onClick={() => setOpts({ ...opts, notifyOnSign: !opts.notifyOnSign })} /> Tell me each time someone signs</label>
          <label className="flex items-center gap-2 text-xs font-semibold text-ink/60"><Toggle testid="qs-notify-on-decline" on={opts.notifyOnDecline} onClick={() => setOpts({ ...opts, notifyOnDecline: !opts.notifyOnDecline })} /> Tell me if someone declines</label>
        </div>
      )}
    </div>
  );
}

// The 3-step path for the common case: one PDF, a few signers, fields pre-placed at the foot of
// the last page. Bulk copies, initials/text/checkbox fields live in the advanced editor.
export function ESignQuickSend({ tenantId, tenant, onClose, onSent, onAdvanced }) {
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [pickedDoc, setPickedDoc] = useState("");
  const [req, setReq] = useState(null);
  const [signers, setSigners] = useState([]);
  const [selected, setSelected] = useState(0);
  const [sequential, setSequential] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState({
    cadence: [1, 2, 3, 7].includes(tenant?.signatures_reminder_every_days) ? tenant.signatures_reminder_every_days : 2,
    maxRem: typeof tenant?.signatures_reminder_max === "number" ? tenant.signatures_reminder_max : 5,
    expires: "", notifyOnSign: tenant?.signatures_notify_on_sign === true, notifyOnDecline: tenant?.signatures_notify_on_decline !== false,
  });

  const toSigners = async () => {
    if (!file && !pickedDoc) { toast.error("Upload a PDF or pick one from Documents"); return; }
    if (!title.trim()) { toast.error("Give it a title — that's what signers see"); return; }
    if (req) { setStep(2); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("message", "");
      if (pickedDoc) fd.append("document_id", pickedDoc); else fd.append("file", file);
      const r = await api.post(`/tenants/${tenantId}/sign-requests`, fd);
      setReq(r.data); setStep(2);
    } catch (e) { toast.error(errMsg(e, "Couldn't read that PDF")); }
    finally { setBusy(false); }
  };

  const toReview = () => {
    const ok = signers.filter((s) => (s.name || "").trim());
    if (!ok.length) { toast.error("Add at least one signer"); return; }
    for (const s of ok) if (!(s.phone || "").trim() && !(s.email || "").trim()) { toast.error(`${s.name} needs a mobile number or email`); return; }
    setSigners(footPlacement(ok, req?.page_count));
    setSelected(0);
    setStep(3);
  };

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/sign-requests/${req.id}/send`, {
        signers, sequential, viewers: viewers.filter((v) => (v.name || "").trim() && ((v.phone || "").trim() || (v.email || "").trim())),
        notify_on_sign: opts.notifyOnSign, notify_on_decline: opts.notifyOnDecline,
        message: "", reminder_every_days: opts.cadence, reminder_max: opts.maxRem, expires_at: opts.expires || null,
      });
      toast.success(sequential ? `Sent! ${signers[0].name} is up first — each signature invites the next.` : "Sent! Every signer just got their signing link by text and/or email.");
      onSent();
    } catch (e) { toast.error(errMsg(e, "Couldn't send — your draft is saved, try again")); setBusy(false); }
  };

  const missing = signers.filter((s) => !(s.fields || []).some((f) => f.kind === "signature"));

  // Step 3 opens on the last page with the fields at its foot — scroll the page view there once the image is in.
  useEffect(() => {
    if (step !== 3) return undefined;
    let tries = 0;
    const t = setInterval(() => {
      const box = document.querySelector("[data-testid='sig-place-scroll']");
      const img = document.querySelector("[data-testid='sig-place-page']");
      if (box && img && img.complete && img.naturalHeight > 0) { box.scrollTop = box.scrollHeight; clearInterval(t); }
      if (++tries > 40) clearInterval(t);
    }, 150);
    return () => clearInterval(t);
  }, [step]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4 backdrop-blur-sm sm:p-8" data-testid="qs-wizard">
      <div className="my-4 w-full max-w-6xl rounded-3xl border border-sand-200 bg-sand-50 p-6 shadow-2xl sm:p-8">
        <div className="mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading flex items-center gap-2 text-2xl font-black tracking-tight"><PenLine className="h-6 w-6 text-redline-500" /> Send for signature</h2>
            <div className="mt-2"><Steps step={step} /></div>
          </div>
          <button data-testid="qs-close" onClick={onClose} className="rounded-full p-2 hover:bg-sand-100"><X className="h-5 w-5" /></button>
        </div>

        {step === 1 && <DocumentStep tenantId={tenantId} title={title} setTitle={setTitle} file={file} setFile={setFile} pickedDoc={pickedDoc} setPickedDoc={setPickedDoc} />}
        {step === 2 && <SignersStep tenantId={tenantId} signers={signers} setSigners={setSigners} sequential={sequential} setSequential={setSequential}
          viewers={viewers} setViewers={setViewers} showCc={showCc} setShowCc={setShowCc} />}
        {step === 3 && req && (
          <div className="space-y-4">
            <OptionsLine opts={opts} setOpts={setOpts} sequential={sequential} />
            <div className="flex flex-col gap-4 lg:flex-row">
              <div className="w-full shrink-0 space-y-2 lg:w-[260px]" data-testid="qs-review-signers">
                <p className="text-[11px] font-bold uppercase tracking-wide text-ink/40">Fields are placed at the foot of the last page — drag to move, or click the page to add more.</p>
                {signers.map((s, i) => (
                  <button key={i} type="button" data-testid={`qs-review-signer-${i}`} onClick={() => setSelected(i)}
                    className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm ${selected === i ? "border-ink bg-white shadow-sm" : "border-sand-200 bg-white/60 hover:border-sand-300"}`}>
                    <span className="h-3 w-3 rounded-full" style={{ background: SIGNER_COLORS[i % SIGNER_COLORS.length] }} />
                    <span className="flex-1 truncate font-semibold">{s.name}</span>
                    <span className={`text-[10px] font-bold ${(s.fields || []).some((f) => f.kind === "signature") ? "text-emerald-600" : "text-amber-600"}`}>
                      {(s.fields || []).some((f) => f.kind === "signature") ? `${s.fields.length} field${s.fields.length === 1 ? "" : "s"}` : "NEEDS SIGNATURE"}
                    </span>
                  </button>
                ))}
              </div>
              <PlacementEditor tenantId={tenantId} req={req} signers={signers} setSigners={setSigners} selected={selected} setSelected={setSelected} initialPage={req.page_count || 1} />
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-sand-200 pt-4">
          {step > 1 && (
            <button type="button" data-testid="qs-back" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1 rounded-full border border-sand-300 bg-white px-4 py-2 text-sm font-semibold text-ink hover:border-ink">
              <ChevronLeft className="h-4 w-4" /> Back
            </button>
          )}
          <button type="button" data-testid="qs-advanced" onClick={() => onAdvanced(req ? { ...req, parties: signers, title } : null)}
            className="text-xs font-semibold text-ink/50 hover:text-ink">
            Need everyone to sign their own copy, initials / text / checkbox fields? Open the advanced editor →
          </button>
          <div className="ml-auto">
            {step === 1 && (
              <button type="button" data-testid="qs-continue-1" onClick={toSigners} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white hover:bg-ink-700 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />} Continue — who signs
              </button>
            )}
            {step === 2 && (
              <button type="button" data-testid="qs-continue-2" onClick={toReview} className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white hover:bg-ink-700">
                <ChevronRight className="h-4 w-4" /> Continue — review
              </button>
            )}
            {step === 3 && (
              <button type="button" data-testid="qs-send" onClick={send} disabled={busy || missing.length > 0} title={missing.length ? `${missing[0].name} needs a signature field` : ""}
                className="inline-flex items-center gap-2 rounded-full bg-redline-500 px-7 py-3 text-sm font-bold text-white hover:bg-redline-600 disabled:opacity-50">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to {signers.length} signer{signers.length === 1 ? "" : "s"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
